/** @jsx jsx */
import React, { Fragment } from 'react'
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react'
import { SwiperWrap, TxtLimit2 } from '@styles/base.style'
import { isMobile } from 'react-device-detect'
import { AdvButton } from '@components/Button'
import { YouTubeEmbed } from 'react-social-media-embed'
import { Button } from '@styles/button.style'
import { SportIssueCardWrap, SportIssueListWrap } from '@views/Matchup/MatchUpDetail/index.style'
import { MATCH_INSIGHT_TYPE, SPORT_ISSUE_TYPE } from '@v2/constants/sport-issue'
import PopupUtils from '@utils/popup-utils'
import { Schema$SportIssue } from '@interface/sport-issue'
import { UploadImgStyle } from '@views/Popup/Chat/index.style'
// img
import popularSearchSlideImg from '@assets/images/bg/sport-issue-popular-search-slide.jpg'
import _ from 'lodash'
import useRootStore from '@store/useRootStore'
import { FreeMode, Pagination } from 'swiper'
import AdvImg from '@components/AdvImg'
import { AnalysisProfile } from '@views/Matchup/MatchUpDetail/TabContent/SportTalkContents/AnalysisProfile'
import Login from '@views/Login'
import { PICK_WRITER_TYPE } from '@v2/constants/common'

interface SportIssueListProps {
  matchId: number
}

export const SportIssueList: React.FC<SportIssueListProps> = observer((props: SportIssueListProps)=> {
  const { matchId } = props
  const { componentStore, matchupStore, userPickStore, companyPickStore, authStore, globalStore, } = useRootStore()
  const { sportIssuePreviewList, sportTalkPickUserInfos } = componentStore
  const isDashboardEnabled = globalStore.getSportTalkDashboardEnabled()
  // 대시보드 팝업 출력 이벤트
  const onClickSportIssueDetailPopup = () => {
    PopupUtils.showSportIssuePopup(matchupStore.detailMatchId)
  }

  const makeSportIssueSlides = (data: Schema$SportIssue[]) => {
    return _.map(data, (item, index) => {

      let contents: JSX.Element
      let onclickSwipeItem: (() => void) | null = null

      if (item.sportIssueType === SPORT_ISSUE_TYPE.POPULAR_SEARCH_TERM_NEWS) {
        contents = <AdvImg src={popularSearchSlideImg} alt="now 이슈키워드" srcMode={'small'}/>
        onclickSwipeItem = () => {
          PopupUtils.showPopularSearchTermPopup(item.popularSearchTerm)
        }
      } else {
        const { matchInsightType, attachment } = item.matchInsight

        if (matchInsightType === MATCH_INSIGHT_TYPE.IMAGE) {
          contents = <AdvImg src={attachment} srcMode={'small'} alt={item.content}/>
          onclickSwipeItem = () => {
            globalStore.pushDialogOk({
              custom: (
                <div css={UploadImgStyle} className={isMobile ? 'is-txt' : 'is-txt pc'}>
                  <img src={attachment} alt={item.content}/>
                  <p className="txt" css={TxtLimit2}>{item.content}</p>
                </div>
              ),
              className: 'image-full-popup',
              singlePress: '닫기',
              // 뒷배경 클릭 시 닫히도록
              callbackCloseDialogBg: () => {
                globalStore.closeDialog()
              },
            })
          }
        } else if (matchInsightType === MATCH_INSIGHT_TYPE.URL) {
          contents = <div className="embed-wrap"><YouTubeEmbed url={attachment} width={'292px'} height={'164px'}/></div>
          onclickSwipeItem = () => {
            globalStore.pushDialogOk({
              custom: (
                <div css={UploadImgStyle} className={isMobile ? 'is-txt' : 'is-txt pc'}>
                  <YouTubeEmbed url={attachment} width={'100%'}/>
                  <p className="txt" css={TxtLimit2}>{item.content}</p>
                </div>
              ),
              className: 'image-full-popup',
              singlePress: '닫기',
              // 뒷배경 클릭 시 닫히도록
              callbackCloseDialogBg: () => {
                globalStore.closeDialog()
              },
            })
          }
        }
      }

      const sportIssueElem = (
        <Fragment>
          {contents}
          <p className="contents-txt">{item.content}</p>
        </Fragment>
      )

      return (
        <SwiperSlide className="swipe-item" key={index}>
          <AdvButton
            width={'100%'}
            contents={sportIssueElem}
            onClick={onclickSwipeItem}
            className="issue-item"
          />
        </SwiperSlide>
      )
    })
  }

  const makeAnalysisSlidesSlides = () => {
    return _.map(sportTalkPickUserInfos, (item, index) => {
      const { userPromiseId, targetId } = item
      const onClickAnalysisBtn = async () => {
        if (!authStore.isSignedIn) {
          authStore.showLoginPopup(<Login/>)
        } else {
          if (item.pickWriterType === PICK_WRITER_TYPE.USER_PICK) { //s 멤버십 경기분석 팝업창
            try {
              await userPickStore.fetchMatch(matchId)
              PopupUtils.showUserPickList({
                targetUserPickId: targetId,
              })
            } catch (error) {
              console.error(error)
            }
          } else if (item.pickWriterType === PICK_WRITER_TYPE.COMPANY_PICK) { //프리미엄 분석가 경기분석 팝업창
            try {
              await companyPickStore.fetchCompanyPick(targetId)
              await companyPickStore.fetchCompanyPickManagers({
                userPromiseIds: [userPromiseId],
              })
              globalStore.setVisibleCompanyPickDetailPopup(true)
            } catch (error) {
              console.error(error)
            }
          }
        }
      }

      return (
        <SwiperSlide className="swipe-item analysis-profile" key={index}>
          <AdvButton
            width={'100%'}
            contents={<AnalysisProfile userInfo={item}/>}
            onClick={onClickAnalysisBtn}
          />
        </SwiperSlide>
      )
    })
  }

  // 스포츠 이슈의 슬라이드 갯수가 5개까지만 출력되게 하도록 추가한 테스트 코드입니다.
  const slides = makeSportIssueSlides(sportIssuePreviewList)
  const analysisSlides = makeAnalysisSlidesSlides()
  let sportIssueListContents: JSX.Element
  if (isMobile) {
    sportIssueListContents = (
      <SwiperWrap className="quick-swipe">
        {slides}
        {isDashboardEnabled && analysisSlides}
      </SwiperWrap>
    )
  } else {
    sportIssueListContents = (
      <SwiperWrap className="p-18 right-navigation">
        <Swiper
          slidesPerView={'auto'}
          pagination={!isMobile && _.size(sportIssuePreviewList) > 0 && { type: 'fraction', clickable: false }}
          navigation={!isMobile}
          spaceBetween={14}
          loop={false}
          shortSwipes
          modules={[FreeMode, Pagination]}
        >
          {slides}
          {isDashboardEnabled && analysisSlides}
        </Swiper>
      </SwiperWrap>
    )
  }

  return (
    <SportIssueListWrap>
      <div className="sport-issue-title">
        <h3 className="tit">대시보드</h3>
        <Button TP onClick={onClickSportIssueDetailPopup} className="all-btn">전체보기</Button>
      </div>
      <SportIssueCardWrap className={!isMobile && 'pc'}>
        {sportIssueListContents}
      </SportIssueCardWrap>
    </SportIssueListWrap>
  )
})
